import { DataSource } from '@angular/cdk/table';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { RecordPermissionDialogComponent } from '@shared/dialogs/record-permission-dialog/record-permission-dialog.component';
import { IOption } from '@shared/models/icommons.model';
import { IRecordAccess } from '@shared/models/iuser.model';
import { DialogService } from '@shared/services/dialog.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'miq-records-permissions',
  templateUrl: './records-permissions.component.html',
  styleUrls: ['./records-permissions.component.scss'],
  standalone: false,
})
export class RecordsPermissionsComponent implements OnInit, OnChanges {
  @Input()
  public recordsArray: IRecordAccess[]; // main records permissions array.
  @Input()
  public recordTypeOptions: IOption[]; // record type options.
  @Input()
  public pageType: string;
  @Input()
  public isPendingUser: boolean;

  public displayedColumns: string[] = [
    // table displayed columns.
    'recordTypeVal',
    'recordID',
    'recordDesc',
    'actions',
  ];
  public dataSubject: BehaviorSubject<IRecordAccess[]>; // behavior subject used between component and table.
  public recordPermissionsDataSource: DataSource<IRecordAccess>; // table data source.

  constructor(public notificationService: DialogService) {
    this.dataSubject = new BehaviorSubject([]);
    this.recordPermissionsDataSource = new RecordPermissionsDataSource(this.dataSubject);
  }

  /**
   * Overrided ngOnInit method.
   */
  public ngOnInit() {
    // If records array does not exists.
    if (!this.recordsArray) {
      // create an empty array.
      this.recordsArray = [];
      this.dataSubject.next(this.recordsArray);
    } else {
      // display array into the table.
      this.dataSubject.next(this.recordsArray);
    }
    if (this.pageType === 'Admin' && this.isPendingUser) {
      this.displayedColumns.splice(3, 0, 'Approve/Reject');
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.recordsArray && !!changes.recordsArray.currentValue) {
      this.recordsArray = changes.recordsArray.currentValue;
      this.dataSubject.next(this.recordsArray);
    }
  }

  /**
   * Add an empty record to the table.
   */
  public addRecord() {
    this.openDialog()
      .pipe(filter(result => !!result))
      .subscribe((result: IRecordAccess) => {
        // TODO: Request for record address.
        this.recordsArray.push(result);
        this.dataSubject.next(this.recordsArray);
      });
  }

  /**
   * Delete a record from the table using the
   * record ID to search for it.
   */
  public deleteRecord(recordToDelete) {
    const indexToDelete = this.recordsArray.map(record => record.recordID).indexOf(recordToDelete);
    this.recordsArray = [...this.recordsArray.slice(0, indexToDelete), ...this.recordsArray.slice(indexToDelete + 1)];
    this.dataSubject.next(this.recordsArray);
  }

  /**
   * Open dialog and return dialog reference.
   *
   * @param record Optional record parameter to edit.
   * @return Dialog reference.
   */
  private openDialog(record?: IRecordAccess): Observable<IRecordAccess> {
    const data = { recordTypesOptions: this.recordTypeOptions, record: record || null };
    return this.notificationService.openDialog(RecordPermissionDialogComponent, data, { panelClass: 'table-modals' });
  }
}

class RecordPermissionsDataSource implements DataSource<IRecordAccess> {
  constructor(private dataSubject: BehaviorSubject<IRecordAccess[]>) {}

  /**
   * DataSource connect overrided method.
   */
  connect(): Observable<IRecordAccess[]> {
    return this.dataSubject.asObservable();
  }

  /**
   * DataSource disconnect overrided method.
   */
  disconnect() {
    /*
     * Nothing to be done on disconnect.
     */
  }
}
